import { useState, useCallback } from 'react';
import { toast } from 'sonner';

import type { AttachmentType } from '@/entities/consignments';
import { validateFile } from '@/shared/lib';

export interface AttachmentUploadFormData {
  file: File | null;
  attachmentType: AttachmentType | null;
  comment?: string;
}

interface UseAttachmentUploadParams {
  onAddAttachment: (file: File, attachmentType: AttachmentType, comment?: string) => void;
  onSuccess?: () => void;
}

export function useAttachmentUpload({ onAddAttachment, onSuccess }: UseAttachmentUploadParams) {
  const [formData, setFormData] = useState<AttachmentUploadFormData>({
    file: null,
    attachmentType: null,
    comment: ''
  });

  const handleFileSelect = useCallback((files: FileList | File[]) => {
    if (files.length > 0) {
      const file = files[0];
      const validation = validateFile(file);
      if (validation.isValid) {
        setFormData((prev) => ({ ...prev, file }));
      }
    }
  }, []);

  const handleRemoveFile = useCallback(() => {
    setFormData((prev) => ({ ...prev, file: null }));
  }, []);

  const handleAttachmentTypeChange = useCallback((value: AttachmentType) => {
    setFormData((prev) => ({ ...prev, attachmentType: value }));
  }, []);

  const handleCommentChange = useCallback((value: string) => {
    setFormData((prev) => ({ ...prev, comment: value.slice(0, 256) }));
  }, []);

  const resetForm = useCallback(() => {
    setFormData({
      file: null,
      attachmentType: null,
      comment: ''
    });
  }, []);

  const handleSubmit = useCallback(() => {
    if (!formData.file || !formData.attachmentType) {
      toast.error('Missing required fields', {
        description: 'Please select a file and attachment type'
      });
      return;
    }

    onAddAttachment(formData.file, formData.attachmentType, formData.comment);
    onSuccess?.();
    resetForm();
  }, [formData.file, formData.attachmentType, formData.comment, onAddAttachment, onSuccess, resetForm]);

  return {
    formData,
    handleFileSelect,
    handleRemoveFile,
    handleAttachmentTypeChange,
    handleCommentChange,
    handleSubmit,
    resetForm
  };
}
